/**
 * Sell wizard — 6 steps. Plus MyOrders (Buying/Selling) and MyListings.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Camera, X, Plus, Lock, Package, ShoppingBag, MoreHorizontal, Share2 } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { ProductCard } from "./Marketplace";
import { ReviewButtonInline } from "./ReviewSheet";
import { toast } from "sonner";
import BottomNav from "../../components/layout/BottomNav";
import RoleSwitcher from "../../components/common/RoleSwitcher";

const CATEGORIES = [
  { id: "equipment", label: "Equipment", desc: "Weights, machines, bands, yoga gear" },
  { id: "diet_food", label: "Diet Food", desc: "Protein bars, organic food — sealed only" },
  { id: "merchandise", label: "Merchandise", desc: "Bottles, bags, accessories, shakers" },
  { id: "supplements", label: "Supplements", desc: "Brand sellers only — FSSAI required", locked: true },
  { id: "clothing", label: "Clothing", desc: "Brand sellers only — hygiene rule", locked: true },
];
const CONDS = [
  { id: "new", label: "New" },
  { id: "like_new", label: "Like New" },
  { id: "good", label: "Good" },
  { id: "fair", label: "Fair" },
];

export function Sell() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    category: null,
    photos: [],
    title: "",
    description: "",
    condition: null,
    tags: "",
    price_inr: "",
    original_price_inr: "",
    delivery_available: false,
    delivery_charge_inr: 0,
    city: user?.city || "",
    pincode: "",
  });
  const [suggestion, setSuggestion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [published, setPublished] = useState(null);
  const inputRef = useRef(null);

  const set = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (step === 4 && data.category) {
      apiClient.get(`/marketplace/price-suggestion?category=${data.category}&title=${encodeURIComponent(data.title)}`)
        .then((r) => setSuggestion(r.data)).catch(() => setSuggestion(null));
    }
  }, [step, data.category, data.title]);

  const readFile = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });

  const addPhoto = async (file) => {
    if (!file) return;
    const b64 = await readFile(file);
    set("photos", [...data.photos, b64].slice(0, 5));
  };

  const removePhoto = (i) => set("photos", data.photos.filter((_, idx) => idx !== i));

  const publish = async () => {
    setBusy(true);
    try {
      const res = await apiClient.post("/marketplace/products", {
        category: data.category,
        title: data.title.trim(),
        description: data.description.trim(),
        price_inr: Number(data.price_inr),
        original_price_inr: data.original_price_inr ? Number(data.original_price_inr) : null,
        condition: ["equipment", "diet_food"].includes(data.category) ? data.condition : null,
        photos: data.photos,
        city: data.city.trim(),
        pincode: data.pincode.trim() || null,
        delivery_available: data.delivery_available,
        delivery_charge_inr: Number(data.delivery_charge_inr || 0),
        pickup_only: !data.delivery_available,
        stock_count: 1,
        tags: data.tags.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 5),
      });
      setPublished(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish");
    } finally {
      setBusy(false);
    }
  };

  if (published) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="sell-success" style={{ background: "#F4F3F1" }}>
        <h1 className="font-barlow-cond font-black" style={{ fontSize: 32, lineHeight: 1.05, color: "#1A1A1A" }}>Your item is live</h1>
        <p className="text-[14px] text-ink-soft mt-2">Share it to get faster sales</p>
        <div className="mt-6 w-full max-w-sm">
          <ProductCard product={{ ...published, is_saved: false, seller_name: user?.name }} onOpen={() => {}} onAddToCart={() => {}} onSave={() => {}} />
        </div>
        <div className="mt-6 space-y-2 w-full max-w-sm">
          <button onClick={() => navigator.share?.({ title: published.title, url: `${window.location.origin}/marketplace/product/${published.product_id}` }).catch(() => {})} data-testid="share-listing" className="w-full h-11 rounded-pill font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}><Share2 size={14} className="inline mr-1" /> Share</button>
          <button onClick={() => navigate("/marketplace/my-listings")} className="w-full h-11 rounded-pill border border-line2" style={{ borderRadius: 100 }}>View my listings</button>
        </div>
      </div>
    );
  }

  const canNext = {
    1: !!data.category,
    2: data.photos.length > 0,
    3: data.title.trim().length >= 3 && data.description.trim().length > 0 && (["equipment","diet_food"].includes(data.category) ? !!data.condition : true),
    4: Number(data.price_inr) > 0,
    5: data.city.trim().length > 0,
    6: true,
  }[step];

  return (
    <div className="app-page min-h-screen pb-28" data-testid="sell-page" style={{ background: "#F4F3F1" }}>
      <header className="sticky top-0 z-20 bg-canvas/90 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto h-14 px-3 flex items-center gap-2">
          <button onClick={() => step === 1 ? navigate(-1) : setStep(step - 1)} aria-label="Back" data-testid="sell-back" className="w-10 h-10 grid place-items-center"><ChevronLeft size={22} /></button>
          <div className="flex-1 h-1.5 bg-line2 rounded-full overflow-hidden"><div className="h-full" style={{ width: `${(step / 6) * 100}%`, background: "#00D4FF" }} /></div>
          <RoleSwitcher variant="light" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-5">
        {step === 1 && (
          <div data-testid="sell-step-1">
            <h2 className="font-barlow-cond font-bold" style={{ fontSize: 22 }}>What are you selling?</h2>
            <div className="mt-4 space-y-2">
              {CATEGORIES.map((c) => (
                <button key={c.id} disabled={c.locked} onClick={() => { set("category", c.id); setStep(2); }} data-testid={`cat-${c.id}`} className="w-full bg-white border border-line2 rounded-[14px] p-4 text-left flex items-center gap-3 disabled:opacity-50">
                  <div className="flex-1">
                    <div className="font-semibold text-[15px]">{c.label} {c.locked && <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold text-ink-tertiary"><Lock size={10} /> Brands only</span>}</div>
                    <div className="text-[12px] text-ink-soft mt-0.5">{c.desc}</div>
                  </div>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => navigate("/marketplace/sell/brand-apply")}
              data-testid="apply-as-brand"
              className="mt-5 w-full bg-white border border-line2 rounded-[14px] p-4 text-left flex items-center gap-3"
              style={{ borderLeft: "3px solid #00D4FF" }}
            >
              <div className="flex-1">
                <div className="font-semibold text-[15px]">Apply as a Brand</div>
                <div className="text-[12px] text-ink-soft mt-0.5">
                  Verified brands can list supplements & clothing, and get higher placement.
                </div>
              </div>
              <span className="text-[13px] font-bold" style={{ color: "#00B0D5" }}>→</span>
            </button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="sell-step-2">
            <h2 className="font-barlow-cond font-bold" style={{ fontSize: 22 }}>Add photos</h2>
            <p className="text-[13px] text-ink-soft mt-1 mb-3">First photo is your cover image. Min 1, max 5.</p>
            <div className="space-y-2">
              <div onClick={() => inputRef.current?.click()} className="relative rounded-[12px] bg-white border-2 border-dashed border-line2 grid place-items-center overflow-hidden" style={{ height: 180 }} data-testid="cover-slot">
                {data.photos[0] ? <img src={data.photos[0]} alt="" className="w-full h-full object-cover" /> : <div className="text-ink-tertiary text-[13px] flex items-center gap-2"><Camera size={20} /> Tap to add cover photo</div>}
                {data.photos[0] && <button onClick={(e) => { e.stopPropagation(); removePhoto(0); }} className="absolute top-2 right-2 w-8 h-8 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}><X size={14} /></button>}
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[1,2,3,4].map((i) => (
                  <div key={i} onClick={() => inputRef.current?.click()} className="relative rounded-[10px] bg-white border border-dashed border-line2 aspect-square grid place-items-center overflow-hidden" data-testid={`slot-${i}`}>
                    {data.photos[i] ? <><img src={data.photos[i]} alt="" className="w-full h-full object-cover" /><button onClick={(e) => { e.stopPropagation(); removePhoto(i); }} className="absolute top-1 right-1 w-6 h-6 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}><X size={12} /></button></> : <Plus size={16} className="text-ink-tertiary" />}
                  </div>
                ))}
              </div>
              <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => addPhoto(e.target.files?.[0])} data-testid="photo-input" />
            </div>
            <p className="text-[11px] text-ink-tertiary mt-3">Good lighting · Show condition clearly · Multiple angles</p>
          </div>
        )}

        {step === 3 && (
          <div data-testid="sell-step-3" className="space-y-3">
            <h2 className="font-barlow-cond font-bold" style={{ fontSize: 22 }}>Describe your item</h2>
            <Field label="Title" value={data.title} onChange={(v) => set("title", v.slice(0, 80))} testid="title" hint={`${data.title.length}/80`} />
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-ink-tertiary font-semibold mb-1">Description</div>
              <textarea value={data.description} onChange={(e) => set("description", e.target.value.slice(0, 500))} rows={5} data-testid="description" className="w-full p-3 rounded-[10px] border border-line bg-canvas2 outline-none text-[14px]" />
              <div className="text-[11px] text-ink-tertiary mt-0.5 text-right">{data.description.length}/500</div>
            </div>
            {["equipment", "diet_food"].includes(data.category) && (
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-ink-tertiary font-semibold mb-2">Condition</div>
                <div className="flex gap-2 flex-wrap">
                  {CONDS.map((c) => {
                    const on = data.condition === c.id;
                    return <button key={c.id} onClick={() => set("condition", c.id)} data-testid={`cond-${c.id}`} className="h-10 px-4 rounded-pill text-[13px]" style={{ background: on ? "#00D4FF" : "#ECEAE7", color: on ? "#0B0B0B" : "#6B6760", fontWeight: on ? 700 : 600, borderRadius: 100 }}>{c.label}</button>;
                  })}
                </div>
              </div>
            )}
            <Field label="Tags (optional, comma separated)" value={data.tags} onChange={(v) => set("tags", v)} testid="tags" />
          </div>
        )}

        {step === 4 && (
          <div data-testid="sell-step-4" className="space-y-3">
            <h2 className="font-barlow-cond font-bold" style={{ fontSize: 22 }}>Set your price</h2>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-ink-tertiary font-semibold mb-1">Price</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-barlow-cond font-extrabold" style={{ fontSize: 28 }}>₹</span>
                <input type="number" value={data.price_inr} onChange={(e) => set("price_inr", e.target.value)} data-testid="price" className="w-full h-16 pl-10 pr-3 rounded-[12px] border border-line bg-white outline-none font-barlow-cond font-extrabold" style={{ fontSize: 28 }} />
              </div>
            </div>
            {suggestion?.available && (
              <button onClick={() => set("price_inr", suggestion.midpoint)} data-testid="use-suggestion" className="w-full bg-white border border-line2 rounded-[10px] p-3 text-left">
                <div className="text-[13px] text-ink-soft">Similar items sell for ₹{suggestion.min.toLocaleString("en-IN")} – ₹{suggestion.max.toLocaleString("en-IN")}</div>
                <div className="text-[12px] text-accent-blue font-semibold mt-0.5" style={{ color: "#00B0D5" }}>Use suggested price ₹{suggestion.midpoint.toLocaleString("en-IN")}</div>
              </button>
            )}
            <Field label="Original price (optional)" value={data.original_price_inr} onChange={(v) => set("original_price_inr", v)} testid="original-price" type="number" />
            <button onClick={() => set("delivery_available", !data.delivery_available)} data-testid="delivery-toggle" className="w-full bg-white border border-line2 rounded-[10px] p-3 flex items-center justify-between">
              <div className="text-left"><div className="text-[13.5px] font-semibold">Offer delivery</div><div className="text-[11.5px] text-ink-tertiary">{data.delivery_available ? "You'll arrange delivery" : "Pickup only"}</div></div>
              <span className="w-10 h-6 rounded-full relative" style={{ background: data.delivery_available ? "#00D4FF" : "#C7C4BF" }}>
                <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white" style={{ left: 2, transform: data.delivery_available ? "translateX(16px)" : "none" }} />
              </span>
            </button>
            {data.delivery_available && <Field label="Delivery charge (0 for free)" value={data.delivery_charge_inr} onChange={(v) => set("delivery_charge_inr", v)} testid="delivery-charge" type="number" />}
          </div>
        )}

        {step === 5 && (
          <div data-testid="sell-step-5" className="space-y-3">
            <h2 className="font-barlow-cond font-bold" style={{ fontSize: 22 }}>Where are you?</h2>
            <Field label="City" value={data.city} onChange={(v) => set("city", v)} testid="city" />
            <Field label="Pincode (optional)" value={data.pincode} onChange={(v) => set("pincode", v)} testid="pincode" />
          </div>
        )}

        {step === 6 && (
          <div data-testid="sell-step-6">
            <h2 className="font-barlow-cond font-bold" style={{ fontSize: 22 }}>Review your listing</h2>
            <div className="text-[11px] uppercase tracking-wider text-ink-tertiary font-semibold mt-3 mb-2">Preview</div>
            <div className="max-w-[200px]">
              <ProductCard
                product={{
                  product_id: "preview",
                  title: data.title,
                  category: data.category,
                  price_inr: Number(data.price_inr) || 0,
                  original_price_inr: data.original_price_inr ? Number(data.original_price_inr) : null,
                  photos: data.photos,
                  city: data.city,
                  condition: data.condition,
                  seller_type: "individual",
                  delivery_available: data.delivery_available,
                  is_saved: false,
                }}
                onOpen={() => {}} onAddToCart={() => {}} onSave={() => {}}
              />
            </div>
            <div className="mt-5 bg-canvas2 border border-line2 rounded-[12px] p-3 text-[13px] text-ink-soft">
              FitFrik charges 5% on sale. You receive: <strong>₹{Math.round((Number(data.price_inr) || 0) * 0.95).toLocaleString("en-IN")}</strong>
              <div className="text-[11.5px] text-accent-blue mt-1" style={{ color: "#00B0D5" }}>Your first 3 listings are commission-free.</div>
            </div>
          </div>
        )}
      </main>

      <div className="fixed left-0 right-0 bottom-0 z-30" style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(20px)", borderTop: "1px solid #E2E0DC", padding: "12px 16px", paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
        {step < 6 ? (
          <button disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="sell-next" className="w-full h-12 rounded-pill font-bold disabled:opacity-50" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>Next</button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setStep(1)} data-testid="sell-edit" className="flex-1 h-12 rounded-pill border border-line2 font-medium" style={{ borderRadius: 100 }}>Edit</button>
            <button onClick={publish} disabled={busy} data-testid="sell-publish" className="flex-[1.4] h-12 rounded-pill font-bold disabled:opacity-50" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>{busy ? "Publishing…" : "Publish Listing"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, hint, type = "text" }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1"><div className="text-[10.5px] uppercase tracking-wider text-ink-tertiary font-semibold">{label}</div>{hint && <div className="text-[10.5px] text-ink-tertiary">{hint}</div>}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full h-11 px-3 rounded-[10px] border border-line bg-canvas2 outline-none text-[14px]" />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// My Orders
// ──────────────────────────────────────────────────────────────
export function MyOrders() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("buying");
  const [orders, setOrders] = useState([]);

  const load = () => apiClient.get(`/marketplace/orders?role=${tab}`).then((r) => setOrders(r.data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, [tab]);

  const statusColour = { confirmed: "#00B0D5", shipped: "#C08400", delivered: "#00A067", cancelled: "#E5484D" }[1] || "#6B6760";

  const act = async (orderId, path) => {
    try {
      await apiClient.post(`/marketplace/orders/${orderId}/${path}`);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  return (
    <div className="app-page min-h-screen pb-24" data-testid="my-orders" style={{ background: "#F4F3F1" }}>
      <header className="sticky top-0 z-20 bg-canvas/90 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto h-14 px-3 flex items-center gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="orders-back" className="w-10 h-10 grid place-items-center"><ChevronLeft size={22} /></button>
          <h1 className="font-display text-[20px]">My Orders</h1>
        </div>
        <div className="max-w-2xl mx-auto px-3 pb-3 flex gap-2">
          {[["buying","Buying"],["selling","Selling"]].map(([id,l]) => {
            const on = tab === id;
            return <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`} className="flex-1 h-9 rounded-pill text-[13px]" style={{ background: on ? "#00D4FF" : "#ECEAE7", color: on ? "#0B0B0B" : "#6B6760", fontWeight: on ? 700 : 600, borderRadius: 100 }}>{l}</button>;
          })}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-4 space-y-2">
        {orders.length === 0 && (
          <div className="text-center py-16" data-testid="orders-empty">
            <Package size={36} className="text-ink-tertiary mx-auto" />
            <div className="font-barlow-cond text-[22px] font-bold mt-3">No orders yet</div>
            {tab === "buying" && <button onClick={() => navigate("/marketplace")} className="mt-4 px-5 h-11 rounded-pill font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>Start shopping</button>}
          </div>
        )}
        {orders.map((o) => (
          <div key={o.order_id} className="bg-white border border-line2 rounded-[14px] p-3" data-testid={`order-${o.order_id}`}>
            <button
              type="button"
              onClick={() => navigate(`/marketplace/orders/${o.order_id}`)}
              data-testid={`order-open-${o.order_id}`}
              className="w-full flex gap-3 text-left"
            >
              <img src={(o.items?.[0]?.photo) || ""} className="w-14 h-14 rounded-[8px] object-cover" alt="" />
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold truncate">{o.items?.[0]?.title} {o.items?.length > 1 && `+${o.items.length-1} more`}</div>
                <div className="text-[11.5px] text-ink-tertiary">Order #{o.order_id.slice(-8)}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] font-bold uppercase px-2 h-5 leading-5 rounded-pill" style={{ background: "#ECEAE7", color: statusColour, borderRadius: 100 }}>{(o.status || "").replace("_", " ")}</span>
                  <span className="font-barlow-cond font-extrabold text-[15px]">₹{(tab === "buying" ? o.amount_inr : o.seller_payout_inr || 0).toLocaleString("en-IN")}</span>
                </div>
              </div>
            </button>
            {tab === "selling" && o.status === "confirmed" && (
              <button onClick={() => act(o.order_id, "mark-shipped")} data-testid={`ship-${o.order_id}`} className="mt-2 w-full h-10 rounded-pill text-[13px] font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>Mark as Shipped</button>
            )}
            {tab === "selling" && o.status === "shipped" && (
              <button onClick={() => act(o.order_id, "confirm-delivery")} data-testid={`deliver-${o.order_id}`} className="mt-2 w-full h-10 rounded-pill text-[13px] font-bold border border-line2" style={{ borderRadius: 100 }}>Confirm Delivery</button>
            )}
            {tab === "buying" && o.status === "delivered" && (
              <ReviewButtonInline orderId={o.order_id} onReviewed={load} />
            )}
          </div>
        ))}
      </main>
      <BottomNav />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// My Listings
// ──────────────────────────────────────────────────────────────
export function MyListings() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);

  const load = () => apiClient.get(`/marketplace/my-listings?status=${tab}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [tab]);

  const act = async (pid, path) => {
    try {
      await apiClient.post(`/marketplace/products/${pid}/${path}`);
      load();
    } catch (e) { toast.error("Could not update"); }
  };
  const del = async (pid) => {
    await apiClient.delete(`/marketplace/products/${pid}`);
    load();
  };

  return (
    <div className="app-page min-h-screen pb-28" data-testid="my-listings" style={{ background: "#F4F3F1" }}>
      <header className="sticky top-0 z-20 bg-canvas/90 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto h-14 px-3 flex items-center gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="listings-back" className="w-10 h-10 grid place-items-center"><ChevronLeft size={22} /></button>
          <h1 className="font-display text-[20px]">My Listings</h1>
        </div>
        <div className="max-w-2xl mx-auto px-3 pb-3 flex gap-2">
          {[["active","Active"],["sold","Sold"],["paused","Paused"]].map(([id,l]) => {
            const on = tab === id;
            return <button key={id} onClick={() => setTab(id)} data-testid={`listings-tab-${id}`} className="flex-1 h-9 rounded-pill text-[13px]" style={{ background: on ? "#00D4FF" : "#ECEAE7", color: on ? "#0B0B0B" : "#6B6760", fontWeight: on ? 700 : 600, borderRadius: 100 }}>{l}</button>;
          })}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pt-3 space-y-2">
        {items.length === 0 && (
          <div className="text-center py-16" data-testid="listings-empty">
            <ShoppingBag size={36} className="text-ink-tertiary mx-auto" />
            <div className="font-barlow-cond text-[22px] font-bold mt-3">Nothing here</div>
            <button onClick={() => navigate("/marketplace/sell")} className="mt-4 px-5 h-11 rounded-pill font-bold" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>List an item</button>
          </div>
        )}
        {items.map((p) => (
          <div key={p.product_id} className="bg-white border border-line2 rounded-[14px] p-3 flex gap-3 items-center" data-testid={`listing-${p.product_id}`}>
            <img src={(p.photos || [])[0]} className="w-14 h-14 rounded-[8px] object-cover" alt="" />
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-semibold truncate">{p.title}</div>
              <div className="font-barlow-cond font-extrabold text-[15px]">₹{p.price_inr.toLocaleString("en-IN")}</div>
              <div className="text-[11px] text-ink-tertiary">{p.views_count || 0} views · {p.saves_count || 0} saves</div>
            </div>
            <div className="flex flex-col gap-1">
              {p.status === "active" && <button onClick={() => act(p.product_id, "pause")} data-testid={`pause-${p.product_id}`} className="text-[11px] h-7 px-2.5 rounded-pill border border-line2" style={{ borderRadius: 100 }}>Pause</button>}
              {p.status === "paused" && <button onClick={() => act(p.product_id, "resume")} data-testid={`resume-${p.product_id}`} className="text-[11px] h-7 px-2.5 rounded-pill" style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}>Resume</button>}
              {p.status !== "sold" && <button onClick={() => act(p.product_id, "mark-sold")} data-testid={`sold-${p.product_id}`} className="text-[11px] h-7 px-2.5 rounded-pill border border-line2" style={{ borderRadius: 100 }}>Mark sold</button>}
              <button onClick={() => del(p.product_id)} data-testid={`del-${p.product_id}`} className="text-[11px] h-7 px-2.5 rounded-pill border border-line2 text-red-500" style={{ borderRadius: 100 }}>Delete</button>
            </div>
          </div>
        ))}
      </main>

      <button onClick={() => navigate("/marketplace/sell")} className="fixed bottom-[24px] right-4 z-30 h-12 px-5 rounded-pill flex items-center gap-1.5 font-bold text-[13px]" style={{ background: "#00D4FF", color: "#000", borderRadius: 100, boxShadow: "0 8px 24px rgba(0,212,255,0.4)" }} data-testid="listings-fab">
        <Plus size={16} /> List an item
      </button>
    </div>
  );
}
